import { useState, useEffect } from 'react';
import axios from 'axios';
import { Reveal } from '@/components/Reveal';
import { SEO, createBreadcrumbSchema } from '@/components/SEO';
import { useSettings } from '@/lib/useSettings';
import { API } from '@/lib/data';
import { Link } from 'react-router-dom';
import { Heart, Activity, Scale, Wind, ArrowRight, CheckCircle } from 'lucide-react';

const ICON_MAP = { Heart, Activity, Scale, Wind };

const DEFAULTS = {
  hero_title: 'Sade Düşün, Sade Yaşa',
  hero_description: 'Sade bir yaşam; her şeyden uzakta kalmak değil, her şeyin tam ortasında dengeyi bulmaktır. Prof. Dr. Nilüfer Yıldırım ile birlikte, bilimsel temelli fonksiyonel tıp yaklaşımını yaşamınıza entegre ederek sağlıklı, aktif, dengeli ve esnek bir yaşam inşa edin.',
  hero_button: 'Danışmanlık Randevusu Al',
  pillars_title: 'SADE Felsefesi',
  pillars_subtitle: 'Danışmanlık programımızın temelini oluşturan dört yapı taşı.',
  pillars: [
    { word: 'Sağlıklı', icon: 'Heart', description: 'Bedeninizin ihtiyaçlarını dinlemek, kronik rahatsızlıkların kök nedenlerine inmek ve gerçek anlamda sağlıklı bir yaşam kurmak.' },
    { word: 'Aktif', icon: 'Activity', description: 'Hareketi yaşamın doğal bir parçası haline getirmek; enerjinizi yüksek tutacak, size uygun bir aktivite rutini oluşturmak.' },
    { word: 'Dengeli', icon: 'Scale', description: 'Beslenme, uyku, stres ve sosyal yaşam arasında sürdürülebilir bir denge kurmak; aşırılıklardan uzak, tutarlı bir yaşam.' },
    { word: 'Esnek', icon: 'Wind', description: 'Değişen koşullara uyum sağlayabilen bir beden ve zihin geliştirmek; strese karşı dayanıklılık kazanmak.' },
  ],
  areas_title: 'Danışmanlık Kapsamı',
  areas_subtitle: 'Kişiye özel değerlendirme ile aşağıdaki alanlarda bilimsel temelli rehberlik sunuyoruz.',
  areas: [
    'Kronik yorgunluk ve enerji düşüklüğü değerlendirmesi',
    'Beslenme ve yaşam tarzı optimizasyonu',
    'Stres yönetimi ve HRV destekli biofeedback',
    'Hormonal denge ve antiaging danışmanlığı',
    'Uyku kalitesi iyileştirme stratejileri',
    'Bağırsak sağlığı ve mikrobiyom desteği',
    'Kişiye özel takviye ve detoks programları',
    'Koruyucu sağlık planlaması',
  ],
  cta_title: 'Nasıl Başlarım?',
  cta_description: 'İlk adım bir ön görüşme randevusudur. Bu görüşmede sağlık geçmişiniz, yaşam tarzınız ve hedefleriniz detaylı şekilde değerlendirilir. Ardından size özel bir SADE yaşam planı oluşturulur ve düzenli takiplerle ilerlemeniz desteklenir.',
  cta_button: 'Randevu Talebi Oluştur',
};

export default function ConsultingPage() {
  const settings = useSettings();
  const [page, setPage] = useState(DEFAULTS);

  useEffect(() => {
    axios.get(`${API}/consulting`).then(r => {
      if (r.data) setPage(prev => ({ ...prev, ...r.data }));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <SEO
        title="Sade Düşün Danışmanlık"
        description={page.hero_description}
        schema={createBreadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: 'Sade Düşün' }])}
      />

      {/* Hero */}
      <section className="pt-28 pb-16 sm:pt-36 sm:pb-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
          <Reveal variant="fadeUp">
            <h1 className="font-heading text-[34px] sm:text-[34px] lg:text-[46px] font-bold tracking-tight text-[#1F2937] mb-4">
              {page.hero_title}
            </h1>
            <p className="text-[22px] sm:text-[24px] text-[#5F6B76] leading-relaxed max-w-2xl mx-auto mb-8">
              {page.hero_description}
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F6E7A] text-white text-sm font-medium hover:bg-[#185960] transition-colors shadow-sm"
              data-testid="consulting-hero-cta"
            >
              {page.hero_button}
              <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SADE Felsefesi */}
      <section className="py-16 sm:py-20 bg-[#F5F3EF]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <Reveal variant="fadeUp">
            <div className="text-center mb-12">
              <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold text-[#1F2937] mb-3">{page.pillars_title}</h2>
              <p className="text-[14px] sm:text-[22px] text-[#5F6B76] max-w-xl mx-auto">{page.pillars_subtitle}</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {(page.pillars || []).map((pillar, i) => {
              const IconComp = ICON_MAP[pillar.icon] || Heart;
              return (
                <Reveal key={pillar.word} variant="slideUp" delay={i * 100}>
                  <div className="bg-white rounded-[20px] border border-[#E7E5E4] p-6 h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#1F6E7A]/10">
                        <IconComp size={20} className="text-[#1F6E7A]" />
                      </div>
                      <span className="text-[24px] font-semibold font-heading text-[#1F6E7A]">{pillar.word}</span>
                    </div>
                    <p className="text-[14px] text-[#5F6B76] leading-relaxed">{pillar.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Danışmanlık Kapsamı */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Reveal variant="fadeUp">
            <div className="text-center mb-10">
              <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold text-[#1F2937] mb-3">{page.areas_title}</h2>
              <p className="text-[14px] sm:text-[22px] text-[#5F6B76] max-w-xl mx-auto">{page.areas_subtitle}</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(page.areas || []).map((area, i) => (
              <Reveal key={i} variant="slideUp" delay={i * 60}>
                <div className="flex items-start gap-3 bg-white rounded-2xl border border-[#E7E5E4] px-5 py-4">
                  <CheckCircle size={18} className="text-[#1F6E7A] mt-0.5 shrink-0" />
                  <span className="text-[14px] text-[#1F2937]">{area}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Nasıl Başlarım */}
      <section className="py-16 sm:py-20 bg-[#F5F3EF]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 text-center">
          <Reveal variant="fadeUp">
            <h2 className="font-heading text-[30px] sm:text-[28px] font-semibold text-[#1F2937] mb-3">{page.cta_title}</h2>
            <p className="text-[14px] sm:text-[22px] text-[#5F6B76] leading-relaxed mb-8 max-w-lg mx-auto">
              {page.cta_description}
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1F6E7A] text-white text-sm font-medium hover:bg-[#185960] transition-colors shadow-sm"
              data-testid="consulting-cta-bottom"
            >
              {page.cta_button}
              <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
